import type { PagedProblems, Problem, ProblemListQuery } from '@shared/types/problem';

export const PROBLEM_CANDIDATE_PAGE_SIZE = 200;
export const PROBLEM_CANDIDATE_VISIBLE_LIMIT = 50;

export interface ProblemCandidateFilters {
  category: string;
  theme: string;
  q: string;
}

export type ListProblemsFn = (query: ProblemListQuery) => Promise<PagedProblems>;

export interface FetchUnlinkedProblemCandidatesParams {
  filters: ProblemCandidateFilters;
  excludeIds: string[];
  listProblems: ListProblemsFn;
}

export interface UnlinkedProblemCandidates {
  items: Problem[];
  total: number;
}

function normalize(value: string | null): string {
  return (value ?? '').trim();
}

function compareProblems(a: Problem, b: Problem): number {
  const category = normalize(a.category).localeCompare(normalize(b.category), 'ja');
  if (category !== 0) return category;
  const theme = normalize(a.theme).localeCompare(normalize(b.theme), 'ja');
  if (theme !== 0) return theme;
  return a.name.localeCompare(b.name, 'ja');
}

export function problemPath(problem: Problem): string {
  return `${problem.category ?? '大カテゴリ未設定'} / ${problem.theme ?? '中テーマ未設定'}`;
}

export function buildProblemCandidateQuery(
  filters: ProblemCandidateFilters,
  page: number,
): ProblemListQuery {
  const query: ProblemListQuery = {
    page,
    page_size: PROBLEM_CANDIDATE_PAGE_SIZE,
    sort_by: 'name',
    sort_dir: 'asc',
  };
  const category = filters.category.trim();
  const theme = filters.theme.trim();
  const q = filters.q.trim();
  if (category !== '') query.category = category;
  if (theme !== '') query.theme = theme;
  if (q !== '') query.q = q;
  return query;
}

function matchesClientSideNameFilter(problem: Problem, q: string): boolean {
  const normalized = q.trim().toLowerCase();
  return normalized === '' || problem.name.toLowerCase().includes(normalized);
}

export async function fetchUnlinkedProblemCandidates({
  filters,
  excludeIds,
  listProblems,
}: FetchUnlinkedProblemCandidatesParams): Promise<UnlinkedProblemCandidates> {
  const excluded = new Set(excludeIds);
  const items: Problem[] = [];
  let page = 1;
  let fetched = 0;

  for (;;) {
    const res = await listProblems(buildProblemCandidateQuery(filters, page));
    fetched += res.items.length;

    for (const problem of res.items) {
      if (excluded.has(problem.id)) continue;
      if (!matchesClientSideNameFilter(problem, filters.q)) continue;
      items.push(problem);
    }

    if (res.items.length < PROBLEM_CANDIDATE_PAGE_SIZE || fetched >= res.total) break;
    page += 1;
  }

  items.sort(compareProblems);
  return { items, total: items.length };
}
